/* eslint-disable max-lines */
import React, {
  DependencyList,
  Dispatch,
  ForwardedRef,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import {
  ffLazyLoading,
  hasFeatureFlag,
  Listener,
  logger,
  noop,
  ObjUtil,
  Util
} from 'yougile-util'
import {
  DataObjectId,
  GroupChat,
  GuestPublicInfo,
  Hub,
  newsCompanyId,
  NotificationSettings,
  Report,
  TaskData
} from 'yougile-model'
import {PlatformEvents} from '@api/model/platform-events'
import {LazyLoadClient} from '@client/lazy-load-client'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {SharedBoardsHelper} from '@client/shared-boards-helper'
import {Storage} from '@client/storage'
import {Sync} from '@client/sync'
import {SyncEvents, SyncListener, SyncListenerFilter} from '@client/sync-events'
import {Debouncer} from '@common/debouncer'
import {boardSettingsState} from '@components/board/board-settings/board-settings-store'
import {asyncGetAllReportTasks} from '@components/statistics/stats-reports/stats-reports-get-tasks'
import {
  ChatUtils,
  idTaskRegexp,
  isChat,
  panelRegexp
} from '@extensions/chat-utils'
import {Feedback} from '@extensions/feedback'
import {globals} from '@extensions/globals-base'
import {
  KeyboardHandlers,
  KeyboardHandlersType
} from '@extensions/keyboard-handlers'
import {NotificationSettingsService} from '@extensions/notifications-settings-service'
import {Page} from '@extensions/page'
import {PageService} from '@extensions/page-service'
import {isChrome, isSafari} from '@extensions/platform'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {taskDataFromHub} from '@extensions/task-data-extractor'

export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = Storage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      return initialValue
    }
  })

  const setValue = (newValue: T | ((prevState: T) => T)): void => {
    if (newValue instanceof Function) {
      setStoredValue(value => {
        const valueToStore = newValue(value)
        Storage.setItem(key, JSON.stringify(valueToStore))
        return valueToStore
      })
    } else {
      setStoredValue(newValue)
      Storage.setItem(key, JSON.stringify(newValue))
    }
  }

  return [storedValue, setValue]
}

export const useMountGuard = () => {
  const abortControllerRef = useRef<AbortController>()

  useLayoutEffect(() => {
    abortControllerRef.current = new AbortController()
    return () => abortControllerRef.current?.abort()
  }, [])

  const createMountedCallback = useCallback(
    (callback: () => void) => () =>
      !abortControllerRef.current?.signal.aborted && callback(),
    []
  )

  return {createMountedCallback}
}

export const useForceUpdate = (): (() => void) => {
  const mountedRef = useRef(true)
  const [, setNum] = React.useState<number>()
  const {createMountedCallback} = useMountGuard()

  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )

  const forceUpdateCallback = useCallback(() => {
    if (mountedRef.current) {
      setNum(Date.now())
    }
  }, [])

  return createMountedCallback(forceUpdateCallback)
}

export const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timeout)
  }, [value])

  return debouncedValue
}

const useLazyRef = <T>(getInitialValue: () => T) => {
  const [value] = useState(getInitialValue)
  return useRef(value)
}

function areInputsEqual(
  newInputs: DependencyList,
  lastInputs: DependencyList
): boolean {
  if (newInputs.length !== lastInputs.length) {
    return false
  }

  for (let i = 0; i < newInputs.length; i++) {
    if (newInputs[i] !== lastInputs[i]) {
      return false
    }
  }
  return true
}

export const useMemoOne = <T>(factory: () => T, deps: DependencyList): T => {
  const valueRef = useLazyRef(factory)
  const prevDeps = useRef(deps)
  if (!areInputsEqual(prevDeps.current, deps)) {
    valueRef.current = factory()
  }
  prevDeps.current = deps
  return valueRef.current
}

export const useClickAway = (
  ref: RefObject<HTMLElement | undefined>,
  callback: (event: Event) => void,
  events = ['mousedown', 'touchstart'],
  capture = false
): void => {
  const callbackRef = useRef(callback)
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])
  useEffect(() => {
    const handler = (event: Event): void => {
      const element = ref.current
      element &&
        !element.contains(event.target as Node) &&
        callbackRef.current(event)
    }
    for (const eventName of events) {
      document.addEventListener(eventName, handler, {capture})
    }
    return () => {
      for (const eventName of events) {
        document.removeEventListener(eventName, handler, {capture})
      }
    }
  }, [events, ref])
}

export const useFocusState = (): [boolean, (v: boolean) => () => void] => {
  const focus = React.useRef<boolean>(false)
  const setFocus = (value: boolean) => () => (focus.current = value)
  return [focus.current, setFocus]
}

export const useListener = <T>(
  listener: Listener<(t: T) => void>
): T | undefined => {
  const [data, setData] = React.useState<T>()
  React.useEffect(() => {
    const id = listener.addListener(d => setData(d))
    return () => {
      void listener.removeListener(id)
    }
  }, [])
  return data
}

export const useScrollState = (): [React.RefObject<any>, boolean] => {
  const [top, setTop] = React.useState(true)
  const ref = React.useRef<HTMLElement>(null)
  const onScroll = (): void => {
    if (!ref.current) {
      return
    }
    const newTop = ref.current.scrollTop === 0
    setTop(newTop)
  }
  React.useEffect(() => {
    if (!ref.current) {
      return noop()
    }
    if (ref.current.scrollTop > 0) {
      setTop(true)
    }
    ref.current.addEventListener('scroll', onScroll)
    return () => {
      ref.current?.removeEventListener('scroll', onScroll)
    }
  }, [ref.current])
  return [ref, top]
}

export const useInput = (
  initialValue: string,
  onInput?: () => void
): [string, (e: {target: {value: string}}) => void, (val: string) => void] => {
  const [val, setVal] = React.useState(initialValue)
  const onChange = (e: {target: {value: string}}): void => {
    setVal(e.target.value || '')
    onInput?.()
  }
  return [val, onChange, setVal]
}

export interface BooleanHookState {
  value: boolean

  turnOn(): void

  turnOff(): void

  toggle(): void

  setValue(v: boolean): void
}

export const useBoolean = (initial: boolean): BooleanHookState => {
  const [val, setVal] = React.useState(initial)
  return {
    value: val,
    turnOn: () => setVal(true),
    turnOff: () => setVal(false),
    toggle: () => setVal(v => !v),
    setValue: v => setVal(v)
  }
}

export const noPropagate = (e: React.MouseEvent): void => e.stopPropagation()
export const noDefault = (e: React.MouseEvent): void => e.preventDefault()

export const keyboardHandler =
  (handlers: KeyboardHandlersType) =>
  async (
    e: Pick<
      React.KeyboardEvent,
      | 'key'
      | 'keyCode'
      | 'shiftKey'
      | 'altKey'
      | 'ctrlKey'
      | 'code'
      | 'stopPropagation'
      | 'preventDefault'
      | 'metaKey'
    >
  ): Promise<boolean | void> => {
    if (!handlers) {
      return
    }
    let result: boolean | undefined = undefined
    if (handlers.anyKey) {
      const r0 = await handlers.anyKey({
        key: e.key,
        keyCode: e.keyCode,
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.ctrlKey,
        meta: e.metaKey,
        code: e.code
      })
      if (r0) {
        e.stopPropagation()
        e.preventDefault()
        result = true
      }
    }
    if (handlers.enter && e.key === 'Enter') {
      const r1 = await handlers.enter()
      if (r1) {
        e.stopPropagation()
        e.preventDefault()
        result = true
      }
    } else if (handlers.escape && e.key === 'Escape') {
      const r2 = await handlers.escape()
      if (r2) {
        e.stopPropagation()
        e.preventDefault()
        result = true
      }
    }
    return result
  }

export const useGlobalKeyDown = (
  handlers: KeyboardHandlersType,
  deps: unknown[] = []
): void => {
  React.useEffect(() => {
    KeyboardHandlers.addKeydown(handlers)
    return () => KeyboardHandlers.removeKeydown(handlers)
  }, [...Object.values(handlers || {}), ...deps])
}

export const useContainerEnterKeydown = <T extends HTMLElement>(
  container: React.RefObject<T>,
  handler?: () => void
): void => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Enter' && !event.shiftKey) {
        handler?.()
        event.stopPropagation()
      }
    },
    [handler]
  )

  useEffect(() => {
    const currentCheckbox = container.current

    if (currentCheckbox) {
      currentCheckbox.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      if (currentCheckbox) {
        currentCheckbox.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [handleKeyDown])
}

export const useGlobalKeyUp = (
  handlers: KeyboardHandlersType,
  deps: React.DependencyList = []
): void => {
  React.useEffect(() => {
    KeyboardHandlers.addKeyup(handlers)
    return () => KeyboardHandlers.removeKeyup(handlers)
  }, deps)
}

export const selectAllText = (e: React.MouseEvent): void => {
  const el = e.target as HTMLElement
  const range = document.createRange()
  range.selectNode(el)
  window.getSelection()?.removeAllRanges()
  window.getSelection()?.addRange(range)
}

export const textCursorSelectAll = (
  e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>
): void => {
  const el = e.target
  el.selectionStart = 0
  el.selectionEnd = el.value.length
}

interface UseLocationUpdateEffectOptions {
  page: Page
  board: Hub
  chat: Hub | undefined
}

export const useLocationUpdateEffect = (
  callback?: (opt: UseLocationUpdateEffectOptions) => boolean,
  deps?: unknown[]
): void => {
  const upd = useForceUpdate()
  useEffect(() => {
    const id = PageService.addLocationListener(() => {
      const res = callback?.({
        get page() {
          return PageService.getPage()
        },
        get board() {
          return PageService.getCurrentBoard()
        },
        get chat() {
          return ChatUtils.getChatFromCurrentUrl()
        }
      })

      if (!callback || res) {
        upd()
      }
    })
    return () => PageService.removeLocationListener(id)
  }, deps ?? [])
}

export const usePage = (): Page => {
  const [page, setPage] = useState(PageService.getPage())
  useEffect(() => {
    const listener = PageService.addLocationListener(
      () => setPage(PageService.getPage()),
      false
    )
    return () => {
      PageService.removeLocationListener(listener)
    }
  }, [])
  return page
}

export const useProject = (): Hub | undefined => {
  const [project, setProject] = useState(() => PageService.getCurrentProject())
  useEffect(() => {
    const listenerId = PageService.addLocationListener(
      () => setProject(PageService.getCurrentProject()),
      false
    )
    return () => {
      PageService.removeLocationListener(listenerId)
    }
  }, [])
  return project
}

const getBoardForContext = (): Hub => {
  if (boardSettingsState.isOpened && boardSettingsState.boardId) {
    const boardFromSettings = Sync.getCurrentSandbox().getHub(
      boardSettingsState.boardId
    )
    if (boardFromSettings) {
      return boardFromSettings
    }
  }
  return PageService.getCurrentBoard()
}

export const useBoard = (): Hub => {
  const [board, setBoard] = useState(getBoardForContext)
  useEffect(() => {
    const cb = () => setBoard(getBoardForContext())
    const listenerId = PageService.addLocationListener(cb, false)
    return () => PageService.removeLocationListener(listenerId)
  }, [boardSettingsState.isOpened, boardSettingsState.boardId])
  return board
}

const getCompanyId = (
  type: 'current' | 'profile' | 'news' | 'custom',
  options: {companyId?: DataObjectId}
): DataObjectId | undefined => {
  switch (type) {
    case 'news':
      return newsCompanyId
    case 'profile':
      return Sync.profileId
    case 'current':
      return globals.getCompanyId()
    default:
      return options.companyId
  }
}

type UseSyncEvent = {
  (
    type: 'current' | 'profile' | 'news' | 'custom',
    options: {
      filter?: SyncListenerFilter
      debounceTime?: number
      onlyRemote?: SyncListener['onlyRemote']
    },
    handler: SyncListener['handler'],
    deps?: unknown[]
  ): void
  (
    type: 'custom',
    options: {
      filter?: SyncListenerFilter
      debounceTime?: number
      onlyRemote?: SyncListener['onlyRemote']
      companyId: DataObjectId
    },
    handler: SyncListener['handler'],
    deps?: unknown[]
  ): void
}

export const useSyncEvent: UseSyncEvent = (type, options, handler, deps) => {
  const companyId = getCompanyId(type, options as unknown as any)

  const {filter = '*', debounceTime = 0, onlyRemote = false} = options

  useEffect(() => {
    if (!companyId) {
      return undefined
    }
    const debouncer = new Debouncer(debounceTime)
    const listenerId = SyncEvents.addListener(
      companyId,
      filter,
      x => {
        if (debounceTime > 0) {
          debouncer.runLatest(() => handler(x))
        } else {
          handler(x)
        }
      },
      onlyRemote
    )
    return () => SyncEvents.removeListener(listenerId)
  }, deps ?? [])
}

export const useSyncEventUpdateEffect = (
  type: 'current' | 'profile' | 'news' | 'custom',
  options: {
    filter?: SyncListenerFilter
    debounceTime?: number
    deps?: unknown[]
    companyId?: DataObjectId
  }
): void => {
  const upd = useForceUpdate()
  useSyncEvent(type, options, upd, options.deps)
}

export const useLoadedLazyChatMessages = ({
  enabled,
  hub,
  forMessage,
  type
}: {
  enabled: boolean
  hub: Hub | undefined
  forMessage: number
  type: 'before' | 'after'
}): void => {
  useEffect(() => {
    if (enabled && hub) {
      const load = async (): Promise<void> => {
        void LazyLoadClient.getMessages(
          hub,
          forMessage,
          type === 'before' ? 50 : 0,
          type === 'after' ? 50 : 0
        )
      }

      void load().catch(e => logger.error(e))
    }
  }, [enabled, hub?.id, forMessage, type])
}

export const useLoadedLazyTaskEffect = (
  hub: Hub | Hub[] = [],
  reason: string
): void => {
  const hubs = Array.isArray(hub) ? hub : [hub]

  useEffect(() => {
    if (!hub) {
      return
    }
    for (const hub of hubs) {
      const alreadyLoadedHub = hub && !hub.isLazy
      if (!alreadyLoadedHub) {
        LazyLoadClient.waitLoad(
          Sync.get(hub),
          hub.id,
          reason ?? 'useLoadedLazyTaskEffect'
        )
          .then(noop)
          .catch(noop)
      }
    }
  }, [
    hubs.length,
    hubs.map(h => h.id).join('|'),
    hubs.map(h => h.isLazy).join('|')
  ])

  useSyncEventUpdateEffect('current', {
    filter: hubs.map(h => h.id),
    debounceTime: 1000
  })
}

export const useCallWhenChanged = <T>(
  checked: T,
  cb: (checked: T) => void
): void => {
  const checkedRef = useRef(checked)

  useLayoutEffect(() => {
    if (checkedRef.current !== checked) {
      checkedRef.current = checked

      cb(checked)
    }
  }, [checkedRef, checked, cb])
}

export const useGuestInfo = (
  guestId?: DataObjectId
): GuestPublicInfo | undefined => {
  const [shouldUpdate, setShouldUpdate] = useState(false)
  const [info, setInfo] = useState<GuestPublicInfo | undefined>(() => {
    if (!guestId) {
      return undefined
    }
    const info = SharedBoardsClient.getGuestInfo(guestId)
    setShouldUpdate(typeof info === 'undefined')
    return info
  })

  useEffect(() => {
    if (guestId) {
      const info = SharedBoardsClient.getGuestInfo(guestId)
      setInfo(info)
      setShouldUpdate(typeof info === 'undefined')
    }
  }, [guestId, setInfo, setShouldUpdate])

  // update guest info if a user is a guest and guest info is unavailable
  useEffect(() => {
    if (shouldUpdate && guestId) {
      setShouldUpdate(false)
      const boardId = PageService.getCurrentBoard()?.id
      if (boardId) {
        SharedBoardsHelper.updateGustsInfo(boardId)
          .then(() => {
            const info = SharedBoardsClient.getGuestInfo(guestId)
            setInfo(info)
          })
          .catch(e => logger.error(e))
      }
    }
  }, [shouldUpdate, guestId, setInfo, setShouldUpdate])

  return info
}

/**
 * Updates local and forwarded ref. Useful when you both need access to
 * some element by ref and pass that ref upwards.
 *
 * @returns A function to be passed as an actual ref to an element.
 */
export const useUpdateForwardedRef = <T>(
  ref: MutableRefObject<T> | ((instance: T) => void),
  forwardedRef: ForwardedRef<T>
): ((x: T) => void) =>
  useCallback(
    x => {
      if (typeof ref === 'function') {
        ref(x)
      } else if (ref) {
        ref.current = x
      }

      if (typeof forwardedRef === 'function') {
        forwardedRef(x)
      } else if (forwardedRef) {
        forwardedRef.current = x
      }
    },
    [ref, forwardedRef]
  )

export type UserBrowser = 'Chrome' | 'Safari' | null

export const useUserAgent = (): {browser: UserBrowser} => {
  let browser: UserBrowser = null
  if (isChrome()) {
    browser = 'Chrome'
  } else if (isSafari()) {
    browser = 'Safari'
  }

  return {browser}
}

export type ChatKind =
  | 'personal'
  | 'task'
  | 'news'
  | 'feedback'
  | 'group'
  | 'empty'

export interface UseChatIdWithKind {
  chatId?: DataObjectId
  kind: ChatKind
}

export const getChatKind = (): ChatKind => {
  const url = location.href
  if (idTaskRegexp.test(url)) {
    return 'task'
  }

  const panelMatch = url.match(panelRegexp)

  if (!panelMatch) {
    return 'empty'
  }
  if (panelMatch[1]) {
    return 'task'
  }
  if (panelMatch[2]) {
    return 'personal'
  }
  if (panelMatch[3]) {
    return 'news'
  }
  return 'empty'
}

export const getChatIdWithKind = () => {
  const newUrl = location.href
  const chatId = isChat(newUrl)
    ? (ChatUtils.getChatFromCurrentUrl() || {id: undefined}).id
    : undefined

  let kind: ChatKind = getChatKind()

  if (kind === 'personal' && chatId) {
    const hub = Sync.getProfile().get(chatId)
    if (hub && Feedback.column && hub.location === Feedback.column.id) {
      kind = 'feedback'
    }
  }

  if (kind === 'task' && chatId) {
    const hub = Sync.getCurrentSandbox().get(chatId)
    if (hub && GroupChat.isGroupChat(hub)) {
      kind = 'group'
    }
  }

  return {chatId, kind}
}

export const useChatIdWithKind = (): UseChatIdWithKind => {
  return getChatIdWithKind()
}

export const useOnMount = (cb: () => void): void => {
  const ref = useRef(false)
  useEffect(() => {
    if (!ref.current) {
      ref.current = true
      cb()
    }
  }, [ref, cb])
}

export const useCompanyId = (): DataObjectId => {
  const [companyId, setCompanyId] = useState(() => globals.getCompanyId())

  useEffect(() => {
    const companyHandler = globals.onCompanyChange(() => {
      const newId = globals.getCompanyId()

      if (companyId !== newId) {
        setCompanyId(newId)
      }
    })

    return () => {
      globals.removeCompanyListener(companyHandler)
    }
  }, [companyId])

  return companyId
}

export interface UseInputKeyDown {
  ref: React.MutableRefObject<HTMLElement | undefined>
  save?: () => void
  cancel?: () => void
}

export const useInputKeyDown = (props: UseInputKeyDown): void => {
  useEffect(() => {
    const listener = (ev: KeyboardEvent): void => {
      const key = ev.key.toLowerCase()
      ev.stopPropagation()

      if (key === 'enter') {
        props.save?.()
      }
      if (key === 'escape') {
        props.cancel?.()
      }
    }

    props.ref.current?.addEventListener('keydown', listener)

    return () => {
      props.ref.current?.removeEventListener('keydown', listener)
    }
  }, [props.ref.current, props.save, props.cancel])
}

export const useUrlUpdater = (): void => {
  const forceUpdate = useForceUpdate()

  useEffect(() => {
    let url = PageService.getUrl()

    const listener = PageService.addLocationListener(() => {
      const newUrl = PageService.getUrl()
      if (url !== newUrl) {
        forceUpdate()
      }
      url = newUrl
    })

    return () => {
      PageService.removeLocationListener(listener)
    }
  }, [])
}

export const usePathNameUpdater = (): void => {
  const forceUpdate = useForceUpdate()

  useEffect(() => {
    let path = PageService.getPath()

    const listener = PageService.addLocationListener(() => {
      const newPath = PageService.getPath()
      if (path !== newPath) {
        forceUpdate()
      }
      path = newPath
    })

    return () => {
      PageService.removeLocationListener(listener)
    }
  }, [])
}

export const useDelayedEffect = (
  callback: () => void,
  dependencies: DependencyList,
  delay: number
): void => {
  useEffect(() => {
    const timer = setTimeout(callback, delay)

    return () => clearTimeout(timer)
  }, [...dependencies, delay])
}

export const useTooltipVisible = <T extends HTMLElement>(
  ref: React.RefObject<T>,
  deps: any[] = []
): boolean => {
  const [showTooltip, setShowTitleTooltip] = useState(false)
  useEffect(() => {
    if (ref.current) {
      const threshold = 1
      const {scrollHeight: sh, scrollWidth: sw} = ref.current
      const {height: h, width: w} = ref.current.getBoundingClientRect()
      setShowTitleTooltip(sh - h > threshold || sw - w > threshold)
    }
  }, [ref, setShowTitleTooltip, ...deps])
  return showTooltip
}

export const usePreviousDeep = <T>(value: T): T | null => {
  const [current, setCurrent] = React.useState<T>(value)
  const [previous, setPrevious] = React.useState<T | null>(null)

  if (!ObjUtil.deepEqual(current, value, true)) {
    setPrevious(current)
    setCurrent(Util.clone(value))
  }

  return previous
}

export const useNotificationSettings = (): NotificationSettings => {
  const forceUpdate = useForceUpdate()
  useSyncEvent('profile', {debounceTime: 50}, forceUpdate)
  return NotificationSettingsService.getNotificationSettings()
}

export const useOnWindowResize = (handler: (e: UIEvent) => void) => {
  const handlerRef = useRef(handler)
  useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  useEffect(() => {
    const listener = (e: UIEvent) => handlerRef.current?.(e)
    window.addEventListener('resize', listener)
    return () => window.removeEventListener('resize', listener)
  }, [handlerRef])
}

export const useHoverNative = (ref: RefObject<HTMLElement>) => {
  const [hover, setHover] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) {
      return () => void 0
    }

    const hover = () => setHover(true)
    const out = () => setHover(false)
    el?.addEventListener('mouseover', hover)
    el?.addEventListener('mouseleave', out)
    return () => {
      el?.removeEventListener('mouseover', hover)
      el?.removeEventListener('mouseleave', out)
    }
  }, [ref])
  return hover
}

export const useHover = () => {
  const [hover, setHover] = useState(false)
  const onMouseOver = useCallback(() => setHover(true), [setHover])
  const onMouseOut = useCallback(() => setHover(false), [setHover])
  return {hover, onMouseOver, onMouseOut}
}

export const useDebouncedFunction = <T extends (...params: any[]) => void>(
  fn: T,
  timeout: number
): [T, () => void] => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const taskRef = useRef<() => void>()

  const cancel = useCallback(
    () => timeoutRef.current && clearTimeout(timeoutRef.current),
    [timeoutRef]
  )

  // Run a scheduled task immediately on unmount to not lose scheduled tasks
  useEffect(() => {
    return () => {
      cancel()
      taskRef.current?.()
    }
  }, [taskRef, cancel])

  const ffn = useCallback(
    (...params: any[]) => {
      timeoutRef.current && clearTimeout(timeoutRef.current)
      taskRef.current = undefined

      const task = () => fn(...params)
      taskRef.current = task
      timeoutRef.current = setTimeout(task, timeout)
    },
    [timeoutRef, taskRef, fn, timeout]
  )

  return [ffn as T, cancel]
}

export const useIsOnline = (): boolean => {
  const [online, setOnline] = useState(navigator.onLine)
  useEffect(() => {
    const handler = (): void => setOnline(navigator.onLine)
    window.addEventListener('online', handler)
    window.addEventListener('offline', handler)
    return () => {
      window.removeEventListener('online', handler)
      window.removeEventListener('offline', handler)
    }
  }, [])
  return online
}

export const useAsyncData = <T>(
  getter: () => Promise<T> | T,
  deps: DependencyList
): {
  /** Is data loading and no data present */
  isLoading: boolean
  /** Is new data loading and previous data is present */
  isFetching: boolean
  data: T | undefined
  error: Error | undefined
} => {
  const getterRef = useRef(getter)
  getterRef.current = getter
  const [lastData, setLastData] = useState<T | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)
  const isOnline = useIsOnline()
  const refetchKey = useRef(0)
  const lastRequestTimestamp = useRef(0)

  const isMounted = useRef(true)
  useEffect(
    () => () => {
      isMounted.current = false
    },
    []
  )

  useEffect(() => {
    const getData = async (): Promise<void> => {
      lastRequestTimestamp.current = Date.now()
      refetchKey.current++
      const currentRefetchKey = refetchKey.current
      await Util.wait(300)
      if (!isMounted.current || currentRefetchKey !== refetchKey.current) {
        return
      }
      setError(undefined)
      const currentGetter = getterRef.current
      try {
        setIsFetching(true)
        const data = await currentGetter()
        if (currentRefetchKey !== refetchKey.current) {
          return
        }
        setLastData(data)
        setIsLoading(false)
        setIsFetching(false)
      } catch (e: unknown) {
        setError(e as Error)
        setIsLoading(false)
        setIsFetching(false)
      }
    }

    void getData()
  }, [...deps, isOnline])

  return {isLoading, isFetching, data: lastData, error}
}

export const useReportTasksIds = ({
  report,
  onlyForTasks,
  shouldUpdateOnDataChange
}: {
  report?: Report
  onlyForTasks?: TaskData[]
  /** WARNING! must be the same between renders */
  shouldUpdateOnDataChange: boolean
}): {
  data: DataObjectId[] | undefined
  isLoading: boolean
  isFetching: boolean
} => {
  const currentBoard = PageService.getCurrentBoard()
  const box = Sync.getCurrentSandbox()

  const [updateKey, setUpdateKey] = useState(0)

  useSyncEvent('current', {debounceTime: 1000, filter: '*'}, () => {
    if (shouldUpdateOnDataChange && !hasFeatureFlag(ffLazyLoading)) {
      setUpdateKey(v => v + 1)
    }
  })

  useEffect(() => {
    if (!shouldUpdateOnDataChange) {
      return Util.noop
    }
    const listener = BoardFilters.addListener(() => {
      setUpdateKey(v => v + 1)
    })
    return () => {
      BoardFilters.removeListener(listener)
    }
  }, [currentBoard?.id, shouldUpdateOnDataChange])

  useEffect(() => {
    if (!shouldUpdateOnDataChange) {
      return Util.noop
    }
    const companyId = box.companyId
    const reportId = report?.id

    if (!reportId || !companyId) {
      return Util.noop
    }

    return Sync.onServerApprovedUpdate(box.companyId, report?.id, () => {
      setUpdateKey(v => v + 1)
    })
  }, [report?.id, box.companyId, shouldUpdateOnDataChange])

  return useAsyncData<DataObjectId[]>(async () => {
    if (!report) {
      return []
    }
    const tasks = await asyncGetAllReportTasks(
      box,
      report,
      onlyForTasks,
      currentBoard
    )
    return tasks.map(t => t.id)
  }, [report?.id, onlyForTasks?.length, currentBoard?.id, box, updateKey])
}

export const useTasksDataFromIds = (tasksIds?: DataObjectId[]): TaskData[] => {
  return React.useMemo(() => {
    if (!tasksIds) {
      return []
    }
    const box = Sync.getCurrentSandbox()
    return tasksIds
      .map(id => box.getHub(id))
      .filter(Boolean)
      .map((hub: Hub) => taskDataFromHub(box, hub))
  }, [tasksIds])
}

export const usePlatformEventEffect = (
  eventName: string,
  callback: () => void,
  deps: DependencyList
): void => {
  useEffect(() => {
    try {
      callback()
    } catch (error) {
      logger.error('[usePlatformEventEffect]: initial callback failed:', error)
    }

    return PlatformEvents.on(
      PlatformEvents.events.updateExtensionsInColumn,
      callback
    )
  }, [eventName, ...deps])
}
